import * as React from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  SubmitHandler,
  UseFormGetValues,
  UseFormSetValue,
  useForm,
} from 'react-hook-form';
import { atom, useAtomValue } from 'jotai';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { toDateFormat, useAddressAdditionalFields, useAnalytics } from '@restworld/utils-common';
import { Address, Worker } from '@restworld/utility-types';

import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import {
  EditModalFooter,
  EditModalTitle,
  EditFieldOptionsModal,
  IEditModalProps,
  FieldOptionsViewType,
} from '../profile-edit-modal';
import { useApiSubmitHandler } from '../../hooks';
import { OtherCheckBoxInputField, useBasicProfileInformation } from '../../../worker-application-wizard';
import {
  CheckboxGroup,
  DatePicker,
  FormLabelWrapper,
  Heading,
  HelperTextRElement,
  PhoneInput,
  TextInput,
  isValidPhoneNumber,
  Alert,
  GoogleAutoCompleteInput,
} from '@restworld/ui-ds-base';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';

type IPersonalDetailsEditProps = IEditModalProps;
interface InputProps {
  name: string;
  surname: string;
  email: string;
  birthdate: string;
  address: Address;
  postal_code: string;
  locality: string;
  phone_number: string;
  whatsapp_phone_number: string;
  communication_channel_preferences: string[];
  sex: string;
  other_communication_channel_preference: string;
}
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return {
    name: workerData?.name,
    surname: workerData?.surname,
    email: workerData?.user?.email,
    birthdate: workerData?.birthdate,
    address: { address: workerData?.address },
    phone_number: workerData?.user?.phone_number,
    whatsapp_phone_number: workerData?.user?.whatsapp_phone_number,
    communication_channel_preferences: workerData?.communication_channel_preferences.map(
      (v) => v.communication_channel_preference_var.id
    ),
    sex: workerData?.sex_var?.id,
    other_communication_channel_preference: workerData?.other_communication_channel_preference,
  };
});
// Digits only, so a stored number without its "+" is not mistaken for a drift.
const digitsOf = (phone?: string) => (phone ?? '').replace(/\D/g, '');
// The last eight digits: enough to tell two numbers apart without reporting either.
const tailOf = (phone?: string) => digitsOf(phone).slice(-8);
// --- main ---//
export const PersonalInfoFormContent: React.FunctionComponent<IPersonalDetailsEditProps> = ({
  editPageTitle,
  onClose,
  showAlert,
}) => {
  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const defaultValues = useAtomValue(defaultValuesAtom);
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    formState: { errors, dirtyFields },
    control,
    getValues,
    setValue,
    reset,
  } = useForm<InputProps>({ reValidateMode: 'onSubmit', defaultValues });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({
    onClose,
    type: 'personalInfo',
    editPageType: 'personal_info',
  });
  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    async (data) => {
      try {
        // phone_number is read-only here and changes only through the OTP flow in the account
        // settings, so it is not sent back: any drift in the rendered value would otherwise be
        // saved as a phone change. A drift is still reported, without the numbers, to find its cause.
        const { phone_number: submittedPhoneNumber, ...editableData } = data;
        if (digitsOf(submittedPhoneNumber) !== digitsOf(defaultValues.phone_number)) {
          trackEvent('worker_profile_phone_drift', {
            loaded_digits: digitsOf(defaultValues.phone_number).length,
            submitted_digits: digitsOf(submittedPhoneNumber).length,
            same_tail_as_loaded: tailOf(submittedPhoneNumber) === tailOf(defaultValues.phone_number),
            same_tail_as_submitted_whatsapp: tailOf(submittedPhoneNumber) === tailOf(data.whatsapp_phone_number),
            whatsapp_edited: !!dirtyFields.whatsapp_phone_number,
          });
        }
        const params: Partial<Worker> = {
          ...editableData,
          ...(data.email ? { emails: [{ email: data.email, primary: true }] } : {}),
          ...(data.birthdate ? { birthdate: toDateFormat(data.birthdate, 'yyyy-MM-dd') } : {}),
          communication_channel_preferences: data.communication_channel_preferences?.map((communicationChannelId) => {
            const communicationPreference = workerFieldOptionsData['communicationChannelPreferenceOptions']?.find(
              (v: { id: string }) => v.id === communicationChannelId
            );
            return {
              communication_channel_preference_var: {
                id: communicationChannelId,
                key: communicationPreference?.key,
                label: communicationPreference?.label,
              },
            };
          }),
          ...data.address,
        };
        apiSubmitHandler(params);
      } catch (error) {
        console.error(error, `error in personal info form`);
      }
    },
    [apiSubmitHandler, workerFieldOptionsData, defaultValues.phone_number, dirtyFields, trackEvent]
  );
  const basicProfileInformation = useBasicProfileInformation();
  const nameSurnameInformationInputs = React.useMemo(
    () =>
      [...basicProfileInformation]
        .filter((v) => v.key !== 'email' && v.key !== 'phone_number')
        .map(({ key, rules, title, type, placeholder, message }) => (
          <FormLabelWrapper isFullWidth key={key} id={key} label={title}>
            <Controller
              name={key as keyof Pick<InputProps, 'name' | 'surname'>}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              control={control as unknown as Control<Pick<InputProps, 'name' | 'surname'>, any>}
              rules={rules}
              render={({ field }) => (
                <TextInput
                  id={key}
                  placeholder={placeholder}
                  type={type}
                  helperText={<HelperTextRElement helperText={Object.keys(errors).includes(key) ? message : ``} />}
                  color={Object.keys(errors).includes(key) ? 'failure' : undefined}
                  {...field}
                />
              )}
            />
          </FormLabelWrapper>
        )),
    [basicProfileInformation, control, errors]
  );

  const sexInputField = React.useMemo(
    () => (
      <FormLabelWrapper isFullWidth id="sex" label={t('onboarding-page:gender')}>
        <Controller
          name="sex"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <TextInput
              id="sex"
              placeholder=""
              type="text"
              rightIcon={ChevronDownIcon}
              helperText={
                Object.keys(errors).includes('sex') ? (
                  <HelperTextRElement helperText={t('common:please_chose_an_option')} />
                ) : null
              }
              color={Object.keys(errors).includes('sex') ? 'failure' : undefined}
              readOnly
              {...field}
              value={workerFieldOptionsData?.['sexOptions']?.find((v) => v.id === field.value)?.label || ''}
              onClick={() =>
                toggleFieldOptionsView((p) => ({
                  ...p,
                  showOptionsView: true,
                  options: workerFieldOptionsData?.['sexOptions'],
                  mainTitle: t('onboarding-page:gender'),
                  id: 'sex',
                  selected: field.value,
                }))
              }
            />
          )}
        />
      </FormLabelWrapper>
    ),
    [control, errors, toggleFieldOptionsView, workerFieldOptionsData, t]
  );

  const dobInputField = React.useMemo(
    () => (
      <FormLabelWrapper isFullWidth id="birthdate" label={t('onboarding-page:birth_date')}>
        <Controller
          key="birthdate"
          name="birthdate"
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <DatePicker
              id="birthdate"
              selectRange={false}
              helperText={Object.keys(errors).includes('birthdate') ? t('onboarding-page:this_field_is_required') : ''}
              fieldState={Object.keys(errors).includes('birthdate') ? 'error' : 'normal'}
              maxDate={new Date()}
              locale="it-IT"
              {...field}
              {...(field.value ? { value: new Date(field.value) } : {})}
            />
          )}
        />
      </FormLabelWrapper>
    ),
    [control, errors, t]
  );

  const emailInput = React.useMemo(
    () =>
      [...basicProfileInformation]
        .filter((v) => v.key === 'email')
        .map(({ key, title, type, placeholder, message }) => (
          <FormLabelWrapper key={key} id={key} label={title} isFullWidth>
            <Controller
              name={key as keyof Pick<InputProps, 'email'>}
              control={control as unknown as Control<Pick<InputProps, 'email'>, any>}
              rules={{ required: false }}
              render={({ field }) => (
                <div>
                  <TextInput
                    id={key}
                    placeholder={placeholder}
                    type={type}
                    helperText={<HelperTextRElement helperText={Object.keys(errors).includes(key) ? message : ``} />}
                    color={Object.keys(errors).includes(key) ? 'failure' : undefined}
                    disabled
                    {...field}
                  />
                  <DisabledFieldMessage field="email" />
                </div>
              )}
            />
          </FormLabelWrapper>
        )),
    [basicProfileInformation, control, errors]
  );

  const phoneNumberInputField = React.useMemo(
    () => (
      <FormLabelWrapper isFullWidth id="phone_number" label={t('onboarding-page:phone_number')}>
        <Controller
          key="phone_number"
          name="phone_number"
          control={control}
          // Read-only field: its value comes straight from the backend and the
          // user cannot edit it here (the editable number is whatsapp_phone_number
          // below), so it must never block the form submit. Validating it client
          // side rejected legit numbers the mobile-only validator doesn't accept
          // (landlines, some foreign numbers), surfacing a false "campo obbligatorio".
          rules={{ required: false }}
          render={({ field }) => {
            return (
              <div className="flex flex-col gap-2">
                <PhoneInput
                  priorityCountries={['IT', 'FR', 'ES']}
                  menuDialogStyles={['min-w-full']}
                  variant={Object.keys(errors).includes('phone_number') ? 'error' : 'primary'}
                  supportText={
                    Object.keys(errors).includes('phone_number') ? t('onboarding-page:this_field_is_required') : null
                  }
                  disabled
                  {...{ ...field, value: field.value && !field.value?.includes('+') ? `+${field.value}` : field.value }}
                />
                <DisabledFieldMessage field="phone_number" />
              </div>
            );
          }}
        />
      </FormLabelWrapper>
    ),
    [control, errors, t]
  );

  const whatsappPhoneNumberInputField = React.useMemo(
    () => (
      <FormLabelWrapper isFullWidth id="whatsapp_phone_number" label={t('common:whatsapp_phone_number')}>
        <Controller
          key="whatsapp_phone_number"
          name="whatsapp_phone_number"
          control={control}
          rules={{ required: false, validate: (v) => !v || isValidPhoneNumber(v) }}
          render={({ field }) => {
            return (
              <PhoneInput
                priorityCountries={['IT', 'FR', 'ES']}
                menuDialogStyles={['min-w-full']}
                variant={Object.keys(errors).includes('whatsapp_phone_number') ? 'error' : 'primary'}
                supportText={
                  Object.keys(errors).includes('whatsapp_phone_number')
                    ? t('onboarding-page:this_field_is_required')
                    : null
                }
                {...{ ...field, value: field.value && !field.value?.includes('+') ? `+${field.value}` : field.value }}
              />
            );
          }}
        />
      </FormLabelWrapper>
    ),
    [control, errors, t]
  );

  const communicationChannelPreferences = React.useMemo(
    () => (
      <FormLabelWrapper id="communication_channel_preferences" label={t('onboarding-page:communication_preferences_1')}>
        <Controller
          key="communication_channel_preferences"
          name="communication_channel_preferences"
          rules={{ required: false /* getValues('other_communication_channel_preference') ? false : true */ }}
          control={control}
          render={({ field }) => {
            return (
              <>
                <CheckboxGroup
                  key="communication_channel_preferences"
                  id="communication_channel_preferences"
                  options={
                    workerFieldOptionsData?.['communicationChannelPreferenceOptions']
                      ? [...workerFieldOptionsData['communicationChannelPreferenceOptions']]
                      : []
                  }
                  mode="clickable"
                  variant="box"
                  isWrapList={false}
                  {...{
                    ...field,
                    onChange: (e) => {
                      let checkV: string[] = field.value ? [...field.value] : [];
                      checkV =
                        !checkV.includes(e.target.id) && e.target.checked
                          ? [...checkV, e.target.id]
                          : checkV.filter((v) => v !== e.target.id);
                      field.onChange(checkV.length ? checkV : []);
                    },
                  }}
                />
              </>
            );
          }}
        />
        <Controller
          name="other_communication_channel_preference"
          control={control}
          render={({ field }) => (
            <OtherCheckBoxInputField
              id="other_communication_channel_preference"
              hasOtherOption={{
                matchKey: 'communication_channel',
                fieldKey: 'other_communication_channel_preference',
                placeholder: t('common:other'),
                infoText: t('onboarding-page:other_communication_channel_preference'),
              }}
              setValue={setValue}
              getValues={getValues}
              {...field}
            />
          )}
        />
        {Object.keys(errors).includes('communication_channel_preferences') ? (
          <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
        ) : null}
      </FormLabelWrapper>
    ),
    [control, errors, getValues, setValue, workerFieldOptionsData, t]
  );
  return (
    <>
      <div className="w-full">
        <EditModalTitle editPageTitle={editPageTitle} onClose={onClose} />
        <form autoComplete="off" method="post" className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
          {showAlert ? <Alert text={t('onboarding-page:missing_info_fill_fields')} /> : null}
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row">{nameSurnameInformationInputs}</div>
            <div className="flex flex-col gap-4 md:flex-row md:items-start">
              {sexInputField}
              {dobInputField}
            </div>
            {emailInput}
            {phoneNumberInputField}
            {whatsappPhoneNumberInputField}

            <AddressFormInput errors={errors} getValues={getValues} setValue={setValue} control={control} />
            <hr className="border-gray-300 md:hidden" />
            {communicationChannelPreferences}
          </div>
          <EditModalFooter
            loading={loading}
            onClose={() => {
              reset(defaultValues);
              onClose();
            }}
            editPageType="personal_info"
          />
        </form>
      </div>
      <EditFieldOptionsModal
        {...fieldOptionsView}
        value={fieldOptionsView.selected ?? getValues(fieldOptionsView.id) ?? ''}
        onChange={(value: string) => {
          toggleFieldOptionsView((p) => ({ ...p, selected: value }));
        }}
        onBack={() => {
          toggleFieldOptionsView(defaultModalFieldOptions);
        }}
        onSave={() => {
          setValue(fieldOptionsView.id, fieldOptionsView.selected);
          toggleFieldOptionsView(defaultModalFieldOptions);
        }}
        visible={fieldOptionsView.showOptionsView}
      />
    </>
  );
};

export default PersonalInfoFormContent;
const AddressFormInput = ({
  errors,
  getValues,
  setValue,
  control,
}: {
  errors: FieldErrors<InputProps>;
  getValues: UseFormGetValues<InputProps>;
  setValue: UseFormSetValue<InputProps>;
  control: Control<InputProps>;
}) => {
  const { t } = useTranslation();
  const [address, setAddress] = React.useState<string>('');
  const onChange = (value: string) => setAddress(value);
  // initialFields: the stored profile address already carries lat/lon, so opening the
  // modal must not re-geocode it (billed call); only a new selection geocodes.
  const addressFields = useAddressAdditionalFields({
    addressString: address,
    initialFields: getValues('address') ?? undefined,
    surface: 'worker_profile_home_address',
  });
  React.useEffect(() => {
    if (addressFields && Object.keys(addressFields).length) {
      setValue('address', {
        address: addressFields.address,
        country: addressFields.country,
        address_lat: addressFields.address_lat,
        address_lon: addressFields.address_lon,
        administrative_area_level_1: addressFields.administrative_area_level_1,
        administrative_area_level_2: addressFields.administrative_area_level_2,
        postal_code: addressFields.postal_code,
        locality: addressFields.locality,
      });
    }
  }, [addressFields, setValue]);
  React.useEffect(() => {
    if (getValues('address')?.address) setAddress(getValues('address').address);
  }, [getValues]);
  return (
    <FormLabelWrapper id={'user-address'} label={t('onboarding-page:your_home_address')}>
      <GoogleAutoCompleteInput
        formId="user-address-input"
        key="user-address-input"
        trackingSurface="worker_profile_home_address"
        placeholder="Via Roma, 12, Verona, VR, Italia"
        onSuggestionSelect={({ description }) => {
          onChange(description);
        }}
        control={control}
        formValue={address}
        autoFocus={false}
        helperText={Object.keys(errors).includes('address') && t('onboarding-page:this_field_is_required')}
      />
    </FormLabelWrapper>
  );
};
interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id' | 'selected'> {
  id: 'sex';
  selected: string;
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'sex',
  returnType: 'string',
  selected: '',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'sex',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};

const DisabledFieldMessage: React.FunctionComponent<{
  field: string;
  route?: string;
  title?: string;
}> = ({ field, route = '/impostazioni', title = 'settings' }) => {
  const { t } = useTranslation();

  return (
    <div className="flex gap-1 items-center">
      <div className="flex items-center gap-1">
        <ExclamationCircleIcon className="h-6 w-6 text-primary-800" />
        <Heading size={6}>{t(`onboarding-page:edit_${field}_in`)}</Heading>
      </div>
      <a href={route} target="_blank" rel="noopener noreferrer">
        <Heading size={6} weight="medium" color="muted-foreground" className="underline underline-offset-1">
          {t(`common:${title}`)}
        </Heading>
      </a>
    </div>
  );
};
